import { DRUID_PATH } from '@somnhold/content';
import { cellAtWorld, cellId } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc, SummerRules, SummerState } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { fireFresh, fireLight, fireStep, fireWon, windDown, type CounterLayout, type FireEvent, type FireNet, type FireParams, type KilnLayout, type PineLayout } from './druid-summer-layout.ts';

/**
 * The Slope of the kilns (docs/sendas/druida.md, chapter 2, summer; the grounds and the counterfire's rule in
 * battle/druid-summer-layout.ts).
 *
 * - The kilns (act 1, `kilns`): Hob covers the six kilns with earth one after another, the nearest alight first (a
 *   blow stops him: the path's work, battle/senda.ts, his spot chosen here). Ash wyverns about the slope dive at a kiln
 *   (3 s, a shadow on it) and set it alight unless struck enough meanwhile. A kiln alight smoulders, cracks with white
 *   smoke (3 s) and bursts: hurt round it, Hob too. Won with every kiln covered or burst; lost with three burst, or Hob.
 * - Olmo's pine (act 2, `strip`): Hob fells the strip's trees one after another (the path's work); the great fire comes
 *   up from the valley toward the strip, burning who stays below its line; lost if it gets there first. The wind throws
 *   embers ahead of it at the hero and at Hob's tree (white smoke 3 s where it falls): it burns there a while, and Hob
 *   can't work in it.
 * - The counterfire (act 3, the test, `counter`): the rule (FireNet) on the act's own clock; a tap on a patch sends the
 *   hero to light it with Hob's ember (standing still within reach). The old wyvern dives at a patch whose fire would go
 *   into the pine (3 s: unless struck enough, it lights it) and breathes down a line through the hero (3 s on the
 *   ground). Who stands in a patch alight burns. Won with the pine's heat (the cone opens); lost with three marks or the
 *   great fire in the pine.
 *
 * Cost: a handful of points a tick (the kilns, the embers, the hero's cell against the patches by a sorted lookup); the
 * rule's step is linear in the patches (28 at most).
 */

type St = SummerState;

const fresh = (r: SummerRules): St => ({
  done: -1,
  lost: 0,
  t: 0,
  kiln: (r.kilns?.at ?? []).map(() => 0),
  kt: (r.kilns?.at ?? []).map(() => 0),
  bursts: 0,
  hobK: -1,
  works: 0,
  wy: {},
  wyvNext: 0,
  wyvSent: 0,
  crow: 0,
  wolf: 0,
  front: r.strip?.foot ?? 0,
  embers: [],
  emberNext: 0,
  heat: -100,
  job: -1,
  held: 0,
  boss: 0,
  bt: -1,
  bx: 0,
  by: 0,
  bx2: 0,
  by2: 0,
  breathNext: 0,
  pend: [],
  bent: 0,
  dives: 0,
  lit: 0,
});

/* ---------- rules from the layouts (the fight's builder in campaign/sendas.ts and the drawing build them the same) ---------- */

export const kilnRules = (L: KilnLayout, wyvHp: number): SummerRules => {
  const K = DRUID_PATH.kilns;
  return {
    kind: 'kilns',
    bossHp: 0,
    kilns: {
      wyvHp,
      at: L.kilns.map((h) => hexToWorld(h.q, h.r)),
      spots: L.spots.map((p) => ({ ...p })),
      rim: L.rim.map((p) => ({ ...p })),
      cover: K.cover,
      fuse: K.fuse,
      warn: K.warn,
      burstBp: K.burstBp,
      burstR: K.burstR,
      burstHob: K.burstHob,
      lose: K.lose,
      wyv: { first: K.wyv.first, every: K.wyv.every, dive: K.wyv.dive, scareBp: K.wyv.scareBp, n: K.wyv.n, more: K.wyv.more, max: K.wyv.max },
      crows: { ...K.crows },
    },
  };
};

export const stripRules = (L: PineLayout): SummerRules => {
  const P = DRUID_PATH.strip;
  return {
    kind: 'strip',
    bossHp: 0,
    strip: {
      trees: L.trees.map((h) => hexToWorld(h.q, h.r)),
      spots: L.spots.map((p) => ({ ...p })),
      rim: L.rim.map((p) => ({ ...p })),
      pine: hexToWorld(L.trunk.q, L.trunk.r),
      foot: L.foot,
      line: L.line,
      speed: imax(1, idiv(L.foot - L.line, P.arrive)),
      frontBp: P.frontBp,
      ember: { ...P.ember },
      crows: { ...P.crows },
      wolves: { ...P.wolves },
    },
  };
};

export const counterRules = (L: CounterLayout, R: number, bossHp: number): SummerRules => {
  const C = DRUID_PATH.counter;
  const cells: { c: number; p: number }[] = [];
  L.net.cells.forEach((cs, p) => cs.forEach((h) => cells.push({ c: cellId(R, h.q, h.r), p })));
  cells.sort((a, b) => a.c - b.c);
  return {
    kind: 'counter',
    bossHp,
    counter: {
      net: { cells: L.net.cells.map((cs) => cs.map((h) => ({ ...h }))), band: [...L.net.band], up: L.net.up.map((x) => [...x]), down: L.net.down.map((x) => [...x]), pineUp: [...L.net.pineUp], pineDown: [...L.net.pineDown], foot: [...L.net.foot] },
      cellOf: cells.map((x) => x.c),
      patchOf: cells.map((x) => x.p),
      mid: L.mid.map((p) => ({ ...p })),
      pine: hexToWorld(L.pine.q, L.pine.r),
      rim: L.rim.map((p) => ({ ...p })),
      burn: C.burn,
      catch: C.catch,
      climb: C.climb,
      flip: L.flip,
      start: L.start,
      warn: C.warn,
      throw: C.throw,
      reach: C.reach,
      marks: C.marks,
      heatBp: C.heatBp,
      wyv: { first: C.wyv.first, every: C.wyv.every, dive: C.wyv.dive, scareBp: C.wyv.scareBp, pace: C.wyv.pace },
      breath: { ...C.breath },
      crows: { ...C.crows },
    },
  };
};

const netOf = (r: NonNullable<SummerRules['counter']>): FireNet => r.net as FireNet;
export const fireParams = (r: NonNullable<SummerRules['counter']>): FireParams => ({ burn: r.burn, catch: r.catch, climb: r.climb, flip: r.flip, start: r.start });

/** A nightmare of the act at a point, its life scaled as the fight's are. */
const spawn = (s: BattleState, kind: 'wolf' | 'crow' | 'wyvern', x: number, y: number): Enemy => {
  const e = spawnEnemy(s, kind, 1, false, 0, x, y);
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

const hobOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'hob');
const heroOf = (s: BattleState): Hero | undefined => s.heroes.find((x) => x.downTicks === 0);

/** A blow on Hob that isn't a nightmare's (a kiln bursting, an ember): its blows off his life. */
const hurtHob = (s: BattleState, n: number): void => {
  const hob = hobOf(s);
  if (!hob || hob.hp <= 0 || n <= 0) return;
  hob.hp -= n;
  hob.hit = true;
  hob.held = 0;
  hob.flinch = imax(hob.flinch, 6);
  ev(s, { t: 'senda', kind: 'hit', npc: 'hob', x: hob.x, y: hob.y });
  if (hob.hp <= 0) {
    hob.hp = 0;
    ev(s, { t: 'senda', kind: 'down', npc: 'hob', x: hob.x, y: hob.y });
  }
};

/* ---------- the kilns ---------- */

/** The kiln Hob goes for: the one alight that bursts soonest, else the next in his order not done. */
const hobKiln = (r: NonNullable<SummerRules['kilns']>, w: St): number => {
  let best = -1;
  for (let k = 0; k < w.kiln.length; k++) {
    const v = w.kiln[k] as number;
    if ((v === 1 || v === 2) && (best < 0 || (w.kt[k] as number) + (v === 1 ? r.warn : 0) < (w.kt[best] as number) + ((w.kiln[best] as number) === 1 ? r.warn : 0))) best = k;
  }
  if (best >= 0) return best;
  for (let k = 0; k < w.kiln.length; k++) if (w.kiln[k] === 0) return k;
  return w.kiln.length;
};

const kilnsTick = (s: BattleState, r: NonNullable<SummerRules['kilns']>, w: St, cue: (c: string) => void): void => {
  const hob = hobOf(s);
  // Hob's kiln done (the path's work counted one more): covered.
  const work = s.senda?.work ?? 0;
  if (hob && work > w.works && w.hobK >= 0 && (w.kiln[w.hobK] as number) !== 3 && (w.kiln[w.hobK] as number) !== 4) {
    w.kiln[w.hobK] = 4;
    const p = r.at[w.hobK] as { x: number; y: number };
    ev(s, { t: 'senda', kind: 'cover', npc: 'hob', x: p.x, y: p.y, n: w.hobK });
    cue(`cover${w.kiln.filter((v) => v === 4).length}`);
  }
  w.works = work;
  // The kilns alight: smoulder, crack, burst.
  for (let k = 0; k < w.kiln.length; k++) {
    const v = w.kiln[k] as number;
    if ((v !== 1 && v !== 2) || s.tick < (w.kt[k] as number)) continue;
    const p = r.at[k] as { x: number; y: number };
    if (v === 1) {
      w.kiln[k] = 2;
      w.kt[k] = s.tick + r.warn;
      ev(s, { t: 'senda', kind: 'crack', npc: '', x: p.x, y: p.y, n: k });
      cue(`crack${w.kiln.filter((x) => x >= 2 && x <= 3).length}`);
      continue;
    }
    w.kiln[k] = 3;
    w.bursts++;
    ev(s, { t: 'senda', kind: 'burst', npc: '', x: p.x, y: p.y, n: k });
    cue(`burst${w.bursts}`);
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, p.x, p.y, r.burstR)) damageHero(s, h, mulBp(h.stats.maxHp, r.burstBp), null, 'fire', true);
    if (hob && withinRange(hob.x, hob.y, p.x, p.y, r.burstR)) hurtHob(s, r.burstHob);
    if (w.bursts >= r.lose && w.done < 0) w.lost = 1;
  }
  // Hob's next: set as his work's spot (a new one starts from nothing; one half covered he finishes first).
  if (hob && hob.hp > 0) {
    const half = w.hobK >= 0 && hob.spot === w.hobK && hob.held * 2 >= r.cover && (w.kiln[w.hobK] as number) < 3;
    const k = half ? w.hobK : hobKiln(r, w);
    if (hob.spot !== k) {
      hob.spot = k;
      hob.held = 0;
    }
    w.hobK = k < w.kiln.length ? k : -1;
    // To a kiln alight he runs (half again his pace, on top of the path's walk).
    const sp = r.spots[k];
    const v = w.kiln[k] as number;
    if (sp && (v === 1 || v === 2) && !withinRange(hob.x, hob.y, sp.x, sp.y, 80) && hob.flinch === 0) stepToward(hob, sp.x, sp.y, idiv(s.rules?.senda?.speed ?? 60, 2));
  }
  // The wyverns: `n` from the start, one more each `more` up to `max`.
  const due = imin(r.wyv.max, r.wyv.n + idiv(s.waveT, r.wyv.more));
  while (w.wyvSent < due) {
    const p = r.rim[(w.wyvSent * 2) % r.rim.length] as { x: number; y: number };
    const e = spawnEnemy(s, 'wyvern', 1, false, 0, p.x, p.y, r.wyvHp);
    w.wy[e.id] = [0, -1, s.tick + (w.wyvSent < r.wyv.n ? r.wyv.first + w.wyvSent * idiv(r.wyv.every, 2) : idiv(r.wyv.every, 2)), 0];
    w.wyvSent++;
  }
  // Soot crows out of the smoke.
  if (s.waveT >= r.crows.first && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    const p = r.rim[rngInt(s.rng, r.rim.length)] as { x: number; y: number };
    for (let k = 0; k < r.crows.n; k++) spawn(s, 'crow', p.x + rngRange(s.rng, -400, 400), p.y + rngRange(s.rng, -400, 400));
  }
  if (w.done < 0 && w.lost === 0 && w.kiln.every((v) => v === 3 || v === 4)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'glade', npc: 'hob', x: 0, y: 0 });
    cue('kilnsDone');
  }
};

/** A wyvern of the kilns: about the slope, then a dive at a kiln still smoking (struck enough, it pulls up). */
const kilnWyvern = (s: BattleState, r: NonNullable<SummerRules['kilns']>, w: St, e: Enemy, px: number, cue: (c: string) => void): boolean => {
  const m = w.wy[e.id];
  if (!m) return false;
  e.stun = 0;
  if (m[0] === 1) {
    const k = m[1] as number;
    // Struck enough on the way down: it pulls up.
    if ((m[3] as number) - e.hp >= mulBp(e.maxHp, r.wyv.scareBp)) {
      const p = r.at[k] as { x: number; y: number };
      ev(s, { t: 'senda', kind: 'scare', npc: '', x: p.x, y: p.y, n: k });
      m[0] = 0;
      m[1] = -1;
      m[2] = s.tick + idiv(r.wyv.every, 2);
      return true;
    }
    if (s.tick < (m[2] as number)) {
      e.anim = 'idle';
      return true;
    }
    if (w.kiln[k] === 0) {
      w.kiln[k] = 1;
      w.kt[k] = s.tick + r.fuse;
      w.lit++;
      const p = r.at[k] as { x: number; y: number };
      ev(s, { t: 'senda', kind: 'kilnLit', npc: '', x: p.x, y: p.y, n: k });
      cue(`lit${w.lit}`);
    }
    e.anim = 'attack';
    e.animT = 0;
    m[0] = 0;
    m[1] = -1;
    m[2] = s.tick + r.wyv.every;
    return true;
  }
  // About: toward the kiln it means next, hanging off it until its time.
  if ((m[1] as number) < 0 || w.kiln[m[1] as number] !== 0) {
    const free: number[] = [];
    for (let k = 0; k < w.kiln.length; k++) if (w.kiln[k] === 0 && k !== w.hobK) free.push(k);
    if (!free.length) for (let k = 0; k < w.kiln.length; k++) if (w.kiln[k] === 0) free.push(k);
    if (!free.length) {
      // Nothing left to set alight: round and round in the smoke, high over the slope.
      const a = r.rim[(idiv(s.tick, 48) + e.id) % r.rim.length] as { x: number; y: number };
      e.anim = 'walk';
      m[1] = -1;
      stepToward(e, idiv(a.x * 2, 3), idiv(a.y * 2, 3), px);
      return true;
    }
    m[1] = free[rngInt(s.rng, free.length)] as number;
  }
  const p = r.at[m[1] as number] as { x: number; y: number };
  const ready = s.tick >= (m[2] as number);
  const tx = p.x;
  const ty = p.y - (ready ? 200 : 1800);
  if (!withinRange(e.x, e.y, tx, ty, 300)) {
    e.anim = 'walk';
    stepToward(e, tx, ty, px);
    return true;
  }
  e.anim = 'idle';
  if (!ready) return true;
  m[0] = 1;
  m[2] = s.tick + r.wyv.dive;
  m[3] = e.hp;
  w.dives++;
  ev(s, { t: 'senda', kind: 'dive', npc: '', x: p.x, y: p.y, n: m[1] as number });
  cue(`dive${w.dives}`);
  return true;
};

/* ---------- the strip ---------- */

const stripTick = (s: BattleState, r: NonNullable<SummerRules['strip']>, w: St, cue: (c: string) => void): void => {
  const hob = hobOf(s);
  const h = heroOf(s);
  if (w.done >= 0) return;
  w.front = imax(r.line - 400, w.front - r.speed);
  if (w.bent === 0 && w.front - r.line < r.speed * 12 * 25) {
    w.bent = 1;
    cue('near');
  }
  // The great fire burns who stays below its line.
  if (s.tick - w.heat >= 12) {
    let burnt = false;
    for (const x of s.heroes) {
      if (x.downTicks > 0 || x.y < w.front) continue;
      damageHero(s, x, imax(1, mulBp(x.stats.maxHp, r.frontBp)), null, 'fire', true);
      burnt = true;
    }
    for (const e of w.embers) {
      if (s.tick < e.at || s.tick >= e.until) continue;
      for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, e.x, e.y, r.ember.r)) {
        damageHero(s, x, imax(1, mulBp(x.stats.maxHp, r.ember.heatBp)), null, 'fire', true);
        burnt = true;
      }
    }
    if (burnt) w.heat = s.tick;
  }
  // Embers ahead of the fire: at the hero and at Hob's tree.
  if (s.waveT >= r.ember.first && s.waveT >= w.emberNext) {
    w.emberNext = s.waveT + r.ember.every;
    const at: { x: number; y: number }[] = [];
    if (h) at.push({ x: h.x + rngRange(s.rng, -500, 500), y: h.y + rngRange(s.rng, -500, 500) });
    // (Hob's: by his next tree, every other volley: it holds him up there, it doesn't burn him; he knows fire.)
    const spot = hob ? r.spots[imin(r.spots.length - 1, hob.spot + 1)] : undefined;
    if (spot && r.ember.n > 1 && w.dives % 2 === 1) at.push({ x: spot.x + rngRange(s.rng, -900, 900), y: spot.y - rngRange(s.rng, 300, 900) });
    for (const p of at) {
      if (p.y > w.front) continue;
      w.embers.push({ x: p.x, y: p.y, at: s.tick + r.ember.warn, until: s.tick + r.ember.warn + r.ember.burn });
      ev(s, { t: 'senda', kind: 'emberWarn', npc: '', x: p.x, y: p.y, n: r.ember.warn });
    }
    w.dives++;
    cue(`ember${w.dives}`);
  }
  for (const e of w.embers) {
    if (s.tick !== e.at) continue;
    ev(s, { t: 'senda', kind: 'ember', npc: '', x: e.x, y: e.y });
    for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, e.x, e.y, r.ember.r)) damageHero(s, x, mulBp(x.stats.maxHp, r.ember.hitBp), null, 'fire', true);
  }
  if (w.embers.length && (w.embers[0] as { until: number }).until <= s.tick) w.embers = w.embers.filter((e) => e.until > s.tick);
  // Hob can't fell in the fire: he waits it out.
  if (hob && hob.spot < r.spots.length) {
    const sp = r.spots[hob.spot] as { x: number; y: number };
    for (const e of w.embers) if (s.tick >= e.at && withinRange(sp.x, sp.y, e.x, e.y, r.ember.r)) hob.flinch = imax(hob.flinch, 2);
  }
  // Crows from the smoke; wolves running from the fire, up the slope.
  if (s.waveT >= r.crows.first && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    const p = r.rim[rngInt(s.rng, r.rim.length)] as { x: number; y: number };
    for (let k = 0; k < r.crows.n; k++) spawn(s, 'crow', p.x + rngRange(s.rng, -400, 400), p.y + rngRange(s.rng, -400, 400));
  }
  if (s.waveT >= r.wolves.first && s.waveT >= w.wolf) {
    w.wolf = s.waveT + r.wolves.every;
    for (let k = 0; k < r.wolves.n; k++) spawn(s, 'wolf', (k % 2 === 0 ? -1 : 1) * rngRange(s.rng, 2000, 5000), imin(w.front - 600, 6000));
  }
  if (hob && hob.spot >= r.spots.length) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'glade', npc: 'hob', x: hob.x, y: hob.y });
    cue('stripDone');
  } else if (w.front <= r.line && w.lost === 0) w.lost = 2;
};

/* ---------- the counterfire ---------- */

/** The patch a sim point stands on (−1: none). */
export const patchAt = (s: BattleState, x: number, y: number): number => {
  const r = s.rules?.senda?.summer?.counter;
  if (!r) return -1;
  const c = cellAtWorld(s, x, y);
  let lo = 0;
  let hi = r.cellOf.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const v = r.cellOf[m] as number;
    if (v === c) return r.patchOf[m] as number;
    if (v < c) lo = m + 1;
    else hi = m - 1;
  }
  return -1;
};

/** A patch's cell nearest a point (sim points). */
export const patchNear = (r: NonNullable<SummerRules['counter']>, i: number, x: number, y: number): { x: number; y: number } => {
  let best = r.mid[i] as { x: number; y: number };
  let bd = 0x7fffffff;
  for (const h of r.net.cells[i] as { q: number; r: number }[]) {
    const p = hexToWorld(h.q, h.r);
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** The hero sent to light a patch: to its edge, within reach. */
export const summerOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.summer?.counter;
  const w = s.senda?.summer;
  if (!r || !w?.fire || w.done >= 0 || at < 0 || at >= r.net.cells.length || w.fire.st[at] !== 0) return;
  w.job = at;
  w.held = 0;
  h.target = 0;
  h.forced = false;
  const p = patchNear(r, at, h.x, h.y);
  const d = isqrt(dist2(p.x, p.y, h.x, h.y));
  if (d <= r.reach - 300) {
    orderMove(s, h, h.x, h.y);
    return;
  }
  const k = r.reach - 900;
  orderMove(s, h, p.x + idiv((h.x - p.x) * k, imax(1, d)), p.y + idiv((h.y - p.y) * k, imax(1, d)));
};

const fireEvents = (s: BattleState, r: NonNullable<SummerRules['counter']>, w: St, out: FireEvent[], cue: (c: string) => void): void => {
  for (const o of out) {
    const p = o.k === 'climb' || o.k === 'pine' ? r.pine : (r.mid[o.i] as { x: number; y: number });
    if (o.k === 'lit') ev(s, { t: 'senda', kind: 'patchLit', npc: o.grand ? 'grand' : '', x: p.x, y: p.y, n: o.i });
    else if (o.k === 'black') ev(s, { t: 'senda', kind: 'patchBlack', npc: '', x: p.x, y: p.y, n: o.i });
    else if (o.k === 'mark') {
      ev(s, { t: 'senda', kind: 'scorch', npc: '', x: r.pine.x, y: r.pine.y, n: w.fire?.marks ?? 1 });
      cue(`scorch${w.fire?.marks ?? 1}`);
    } else if (o.k === 'meet') {
      ev(s, { t: 'senda', kind: 'meet', npc: '', x: p.x, y: p.y, n: o.i });
      if ((w.fire?.meets ?? 0) === 1) cue('meet');
    } else if (o.k === 'climb') ev(s, { t: 'senda', kind: 'climb', npc: '', x: 0, y: 0, n: o.i });
    else if (o.k === 'pine') ev(s, { t: 'senda', kind: 'pineBurn', npc: '', x: r.pine.x, y: r.pine.y });
  }
};

/** The patches whose fire the wind would carry into the pine now (grass): where the old wyvern goes. */
const dangerous = (r: NonNullable<SummerRules['counter']>, w: St, down: boolean): number[] => {
  const st = w.fire;
  if (!st) return [];
  const near = down ? r.net.pineUp : r.net.pineDown;
  const out: number[] = [];
  for (let i = 0; i < r.net.cells.length; i++) {
    if (st.st[i] !== 0) continue;
    if (near.includes(i) || ((down ? r.net.down[i] : r.net.up[i]) as number[]).some((j) => near.includes(j) && st.st[j] === 0)) out.push(i);
  }
  return out;
};

const counterTick = (s: BattleState, r: NonNullable<SummerRules['counter']>, w: St, cue: (c: string) => void): void => {
  const st = w.fire;
  if (!st) return;
  const h = heroOf(s);
  const P = fireParams(r);
  const t = w.t;
  const out: FireEvent[] = [];
  // The hero's job by a patch: standing still within reach, the ember thrown.
  if (h && w.job >= 0) {
    if (st.st[w.job] !== 0) w.job = -1;
    else {
      const p = patchNear(r, w.job, h.x, h.y);
      if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
        w.held = 0;
        if (!h.moving) w.job = -1;
      } else if (++w.held >= r.throw) {
        fireLight(st, w.job, t, false, out);
        w.job = -1;
        w.held = 0;
      }
    }
  }
  if (w.done < 0) fireStep(netOf(r), P, st, t, out);
  fireEvents(s, r, w, out, cue);
  // The smoke bends before the wind turns; then it blows down.
  if (w.bent === 0 && t >= r.flip - r.warn) {
    w.bent = 1;
    ev(s, { t: 'senda', kind: 'bend', npc: '', x: r.pine.x, y: r.pine.y, n: r.warn });
    cue('bend');
  }
  if (w.bent === 1 && t >= r.flip) {
    w.bent = 2;
    ev(s, { t: 'senda', kind: 'turn', npc: '', x: r.pine.x, y: r.pine.y });
    cue('turn');
  }
  // Who stands in a patch alight burns.
  if (s.tick - w.heat >= 12) {
    for (const x of s.heroes) {
      if (x.downTicks > 0) continue;
      const i = patchAt(s, x.x, x.y);
      if (i >= 0 && st.st[i] === 1) {
        damageHero(s, x, imax(1, mulBp(x.stats.maxHp, r.heatBp)), null, 'fire', true);
        w.heat = s.tick;
      }
    }
  }
  // The old wyvern's breath landing.
  if (w.bt >= 0 && s.tick >= w.bt) {
    w.bt = -1;
    ev(s, { t: 'senda', kind: 'breath', npc: `${w.bx2},${w.by2}`, x: w.bx, y: w.by });
    const boss = w.boss ? findEnemy(s, w.boss) : null;
    for (const x of s.heroes) if (x.downTicks === 0 && onLine(x.x, x.y, w.bx, w.by, w.bx2, w.by2, idiv(r.breath.w, 2))) damageHero(s, x, mulBp(x.stats.maxHp, r.breath.bp), boss, 'fire', true);
  }
  // Soot crows.
  if (w.done < 0 && s.waveT >= r.crows.first && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    const p = r.rim[rngInt(s.rng, r.rim.length)] as { x: number; y: number };
    for (let k = 0; k < r.crows.n; k++) spawn(s, 'crow', p.x + rngRange(s.rng, -400, 400), p.y + rngRange(s.rng, -400, 400));
  }
  if (w.done < 0 && w.lost === 0) {
    if (st.burnt || st.marks >= r.marks) w.lost = 3;
    else if (fireWon(netOf(r), st)) {
      w.done = s.tick;
      ev(s, { t: 'senda', kind: 'cone', npc: '', x: r.pine.x, y: r.pine.y });
      cue('cone');
    }
  }
  w.t++;
};

/** Whether a point is within `half` of the segment a–b. */
const onLine = (x: number, y: number, ax: number, ay: number, bx: number, by: number, half: number): boolean => {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy;
  if (L2 <= 0) return withinRange(x, y, ax, ay, half);
  // The nearest point's place along the segment, in 1/1024ths.
  const k = imax(0, imin(1024, idiv(((x - ax) * dx + (y - ay) * dy) * 1024, L2)));
  const px = ax + idiv(dx * k, 1024);
  const py = ay + idiv(dy * k, 1024);
  return withinRange(x, y, px, py, half);
};

/** The old wyvern: about the slope near the hero; a dive at a patch whose fire would go into the pine; its breath down a line through the hero. */
const oldWyvern = (s: BattleState, r: NonNullable<SummerRules['counter']>, w: St, e: Enemy, px: number, cue: (c: string) => void): boolean => {
  const m = w.wy[e.id];
  const st = w.fire;
  if (!m || !st) return false;
  e.stun = 0;
  if (w.done >= 0) {
    e.anim = 'idle';
    return true;
  }
  const h = heroOf(s);
  const down = windDown(fireParams(r), w.t);
  // A dive under way.
  if (m[0] === 1) {
    const i = m[1] as number;
    const p = r.mid[i] as { x: number; y: number };
    if ((m[3] as number) - e.hp >= mulBp(e.maxHp, r.wyv.scareBp)) {
      ev(s, { t: 'senda', kind: 'scare', npc: '', x: p.x, y: p.y, n: i });
      m[0] = 0;
      m[2] = s.tick + idiv(r.wyv.every, 2);
      return true;
    }
    if (s.tick < (m[2] as number)) {
      e.anim = 'idle';
      return true;
    }
    if (st.st[i] === 0) {
      const out: FireEvent[] = [];
      fireLight(st, i, w.t, false, out);
      fireEvents(s, r, w, out, cue);
      w.lit++;
      cue(`wyvLit${w.lit}`);
    }
    e.anim = 'attack';
    e.animT = 0;
    m[0] = 0;
    m[2] = s.tick + r.wyv.every;
    return true;
  }
  // Its breath, warned: it holds still while the line burns on the ground.
  if (w.bt >= 0) {
    e.anim = 'attack';
    return true;
  }
  if (h && s.tick >= w.breathNext && withinRange(e.x, e.y, h.x, h.y, r.breath.len - 1500)) {
    w.breathNext = s.tick + r.breath.every;
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    w.bx = e.x;
    w.by = e.y;
    w.bx2 = e.x + idiv(dx * r.breath.len, d);
    w.by2 = e.y + idiv(dy * r.breath.len, d);
    w.bt = s.tick + r.breath.warn;
    e.facing = 0;
    ev(s, { t: 'senda', kind: 'breathWarn', npc: `${w.bx2},${w.by2}`, x: w.bx, y: w.by, n: r.breath.warn });
    w.dives++;
    cue(`breath${w.dives}`);
    return true;
  }
  // Time for a dive: a patch whose fire would go into the pine now.
  if (s.tick >= (m[2] as number)) {
    const list = dangerous(r, w, down);
    if (list.length) {
      if ((m[1] as number) < 0 || !list.includes(m[1] as number)) m[1] = list[rngInt(s.rng, list.length)] as number;
      const p = r.mid[m[1] as number] as { x: number; y: number };
      if (!withinRange(e.x, e.y, p.x, p.y, 400)) {
        e.anim = 'walk';
        stepToward(e, p.x, p.y, imax(px, r.wyv.pace));
        return true;
      }
      m[0] = 1;
      m[2] = s.tick + r.wyv.dive;
      m[3] = e.hp;
      ev(s, { t: 'senda', kind: 'dive', npc: '', x: p.x, y: p.y, n: m[1] as number });
      cue('dive1');
      return true;
    }
    m[2] = s.tick + r.wyv.every;
  }
  // About: off the hero, toward the pine's side.
  if (h) {
    const tx = h.x + (h.x > r.pine.x ? 2600 : -2600);
    const ty = h.y - 2200;
    if (!withinRange(e.x, e.y, tx, ty, 500)) {
      e.anim = 'walk';
      stepToward(e, tx, ty, px);
    } else e.anim = 'idle';
  }
  return true;
};

/* ---------- the act ---------- */

export const summerTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.summer;
  const sr = s.senda;
  if (!r || !sr) return;
  if (!sr.summer) {
    const w = fresh(r);
    sr.summer = w;
    if (r.counter) {
      w.fire = fireFresh(netOf(r.counter));
      w.crow = r.counter.crows.first;
      w.breathNext = r.counter.breath.first;
      const p = r.counter.rim[0] as { x: number; y: number };
      const e = spawnEnemy(s, 'wyvern', 1, false, 0, p.x, p.y, r.bossHp);
      w.boss = e.id;
      s.bossId = e.id;
      w.wy[e.id] = [0, -1, r.counter.wyv.first, 0];
    }
    if (r.strip) w.crow = r.strip.crows.first;
  }
  const w = sr.summer;
  if (!s.started) return;
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  if (r.kilns) kilnsTick(s, r.kilns, w, cue);
  else if (r.strip) stripTick(s, r.strip, w, cue);
  else if (r.counter) counterTick(s, r.counter, w, cue);
};

/** A wyvern up in the smoke (about, not diving, not breathing): out of reach. */
export const summerAloft = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.summer;
  const m = w?.wy[e.id];
  if (!w || !m || m[0] !== 0) return false;
  return !(e.id === w.boss && w.bt >= 0);
};

/** Moved by the act's own rule (true): the kilns' wyverns, the old wyvern. */
export const summerHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.summer;
  const w = s.senda?.summer;
  if (!r || !w || !w.wy[e.id]) return false;
  const cue = (c: string): void => {
    w.pend.push(c);
  };
  if (r.kilns) return kilnWyvern(s, r.kilns, w, e, px(e), cue);
  if (r.counter) return oldWyvern(s, r.counter, w, e, px(e), cue);
  return false;
};

/** Another order of the hero's own: the patch it was sent to is off. */
export const summerMarkOff = (s: BattleState): void => {
  const w = s.senda?.summer;
  if (w) w.job = -1;
};

/** How far the goal has come: the kilns done, the trees felled, the pine's heat (above, below, the fires met). */
export const summerGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.summer;
  const w = s.senda?.summer;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.kilns) return { done: w.kiln.filter((v) => v >= 3).length, need: w.kiln.length };
  if (r.strip) {
    const hob = hobOf(s);
    return { done: imin(r.strip.trees.length, hob?.spot ?? 0), need: r.strip.trees.length };
  }
  const c = r.counter;
  const st = w.fire;
  if (!c || !st) return { done: 0, need: 3 };
  const up = c.net.pineUp.some((i) => st.st[i] === 2) ? 1 : 0;
  const down = c.net.pineDown.every((i) => st.st[i] !== 0) && c.net.pineDown.some((i) => st.st[i] === 2) ? 1 : 0;
  return { done: w.done >= 0 ? 3 : up + down + (st.meets > 0 ? 1 : 0), need: 3 };
};

/** Won a moment after the goal; lost with three kilns burst, the fire at the strip, the pine scorched or burnt. */
export const summerResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.summer;
  const w = s.senda?.summer;
  if (!r || !w) return null;
  if (w.lost) return 'lost';
  if (w.done >= 0) return s.tick >= w.done + (r.counter ? 36 : 18) ? 'won' : null;
  return null;
};
